import { countryToCurrency } from '@/lib/currency';
import type { ListingFormValues, ListingRowForEdit } from './types';
import { normalizeCountryCode, normalizeRegionValue } from './countries';
import { normalizePropertyType } from './propertyTypes';
import { imagesFromListingRow } from './imageModel';
import type { DealConstraintId } from '../../../lib/dealConstraints';
import { DEAL_CONSTRAINTS } from '../../../lib/dealConstraints';
import { normalizeDealType } from '@/lib/dealTypes';
import {
  createEmptyFinancingFields,
  financingFieldsFromRow,
} from '@/lib/financingTerms';

function str(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function numStr(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  const n = Number(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? String(n) : '';
}

const CONSTRAINT_IDS = new Set(DEAL_CONSTRAINTS.map((c) => c.id));

function normalizeConstraints(raw: unknown): DealConstraintId[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (id): id is DealConstraintId =>
      typeof id === 'string' && CONSTRAINT_IDS.has(id as DealConstraintId)
  );
}

export function createEmptyListingFormValues(): ListingFormValues {
  const country = 'US';
  return {
    country,
    address: '',
    city: '',
    state: '',
    zip: '',
    price: '',
    currency: countryToCurrency(country),
    currencyOverridden: false,
    arv: '',
    repairs: '',
    beds: '',
    baths: '',
    sqft: '',
    lot_sqft: '',
    garage_spaces: '',
    year_built: '',
    property_type: '',
    deal_type: '',
    age_restricted: false,
    description: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    deal_constraints: [],
    deal_constraints_notes: '',
    seller_acknowledged: false,
    latitude: null,
    longitude: null,
    pinStatus: 'not_geocoded',
    images: [],
    financing: createEmptyFinancingFields(),
    original_deal_type: '',
  };
}

export function listingRowToFormValues(row: ListingRowForEdit): ListingFormValues {
  const country = normalizeCountryCode(row.country);
  const currencyRaw = row.currency;
  const currency =
    currencyRaw === 'USD' || currencyRaw === 'CAD' || currencyRaw === 'GBP' || currencyRaw === 'AUD'
      ? currencyRaw
      : countryToCurrency(country);

  const lat = typeof row.latitude === 'number' && Number.isFinite(row.latitude) ? row.latitude : null;
  const lng =
    typeof row.longitude === 'number' && Number.isFinite(row.longitude) ? row.longitude : null;
  const dealType = normalizeDealType(row.deal_type) ?? '';

  return {
    country,
    address: str(row.address),
    city: str(row.city),
    state: normalizeRegionValue(country, str(row.state)),
    zip: str(row.zip),
    price: numStr(row.price),
    currency,
    currencyOverridden: true,
    arv: numStr(row.arv),
    repairs: numStr(row.repairs),
    beds: numStr(row.beds),
    baths: numStr(row.baths),
    sqft: numStr(row.sqft),
    lot_sqft: numStr(row.lot_sqft),
    garage_spaces: numStr(row.garage_spaces),
    year_built: numStr(row.year_built),
    property_type: normalizePropertyType(row.property_type),
    deal_type: dealType,
    age_restricted: Boolean(row.age_restricted),
    description: str(row.description),
    contact_name: str(row.contact_name),
    contact_phone: str(row.contact_phone),
    contact_email: str(row.contact_email),
    deal_constraints: normalizeConstraints(row.deal_constraints),
    deal_constraints_notes: str(row.deal_constraints_notes),
    seller_acknowledged: Boolean(row.seller_acknowledged_at),
    latitude: lat,
    longitude: lng,
    pinStatus: lat != null && lng != null ? 'geocoded' : 'not_geocoded',
    images: imagesFromListingRow(row),
    financing: financingFieldsFromRow(row.financing_terms ?? null),
    original_deal_type: dealType,
  };
}
